/**
 * KAN-926: разделы списка "Требуют внимания" (дашборд, /m, мобильная доска).
 *
 * Раздел строки решает backend (поле attention_section); фронт только группирует
 * строки в порядке разделов, не меняя порядок строк внутри раздела.
 * Строка без раздела (старый backend) или с неизвестным кодом идёт в "Новые".
 * Число раздела: из counts (section_counts backend, по всему списку до лимита), когда оно есть,
 * иначе по показанным строкам. Раздел без строк на странице, но с counts > 0, тоже возвращается.
 */
export type AttentionSection = 'new' | 'stale' | 'declined' | 'reopened';

export interface AttentionSectionInfo {
  code: AttentionSection;
  title: string;
}

/** Разделы сверху вниз. */
export const ATTENTION_SECTIONS: readonly AttentionSectionInfo[] = [
  { code: 'new', title: 'Новые' },
  { code: 'stale', title: 'Без движения' },
  { code: 'declined', title: 'Перед отказом' },
  { code: 'reopened', title: 'Возвращённые' },
];

const DEFAULT_SECTION: AttentionSection = 'new';

/** KAN-926: число строк по разделам по всему списку (section_counts backend). */
export type AttentionSectionCounts = Partial<Record<AttentionSection, number>>;

export interface AttentionSectionGroup<T> extends AttentionSectionInfo {
  items: T[];
  /** Число строк раздела: из counts или по items. */
  count: number;
}

function toSection(code: string | null | undefined): AttentionSection {
  return ATTENTION_SECTIONS.some((s) => s.code === code) ? (code as AttentionSection) : DEFAULT_SECTION;
}

/**
 * Строки по разделам в порядке ATTENTION_SECTIONS; раздел без строк и без числа в counts
 * не возвращается.
 */
export function groupAttentionSections<T>(
  items: readonly T[],
  sectionOf: (item: T) => string | null | undefined,
  counts?: AttentionSectionCounts | null
): AttentionSectionGroup<T>[] {
  const buckets = new Map<AttentionSection, T[]>();
  for (const item of items) {
    const code = toSection(sectionOf(item));
    const bucket = buckets.get(code);
    if (bucket) bucket.push(item);
    else buckets.set(code, [item]);
  }
  return ATTENTION_SECTIONS.map((s) => {
    const sectionItems = buckets.get(s.code) ?? [];
    const counted = counts?.[s.code];
    const count = typeof counted === 'number' ? Math.max(counted, sectionItems.length) : sectionItems.length;
    return { ...s, items: sectionItems, count };
  }).filter((group) => group.count > 0);
}
